import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExportButtons } from "@/features/timesheet/components/export-buttons";
import { MonthPicker } from "@/features/timesheet/components/month-picker";
import { PendingNotice } from "@/features/timesheet/components/pending-notice";
import { TimesheetTable } from "@/features/timesheet/components/timesheet-table";
import { WorkerDayDrawer } from "@/features/timesheet/components/worker-day-drawer";
import { useTimesheet } from "@/features/timesheet/hooks/use-timesheet";
import type { TimesheetRow } from "@/features/timesheet/types";
import { currentMonth } from "@/features/timesheet/utils/format";
import { pendingDays } from "@/features/timesheet/utils/totals";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function TimesheetPage() {
	const { obra: obraKey } = useParams();
	const { has } = usePermissions();
	const canUpdate = has("time_entries:update");
	const [month, setMonth] = useState(currentMonth);
	const [selectedWorkerId, setSelectedWorkerId] = useState<string>();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const sheet = useTimesheet(month);

	const rows = sheet.data?.rows ?? [];
	const selectedRow = rows.find((row) => row.worker.id === selectedWorkerId);
	const holidays = new Map(
		(sheet.data?.holidays ?? []).map((holiday) => [holiday.date, holiday.name]),
	);

	function onSelectWorker(row: TimesheetRow) {
		setSelectedWorkerId(row.worker.id);
		setDrawerOpen(true);
	}

	function onMonthChange(next: string) {
		setMonth(next);
		setDrawerOpen(false);
	}

	function body() {
		if (sheet.isPending) {
			return (
				<div className="page-loading">
					<Loader2 />
				</div>
			);
		}
		if (sheet.isError) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar a folha de horas.</p>
						<Button variant="outline" onClick={() => sheet.refetch()}>
							Tentar novamente
						</Button>
					</CardContent>
				</Card>
			);
		}
		if (rows.length === 0) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Nenhuma pessoa com dias lançados neste mês.</p>
					</CardContent>
				</Card>
			);
		}
		return (
			<Card className="timesheet-page__card">
				<TimesheetTable
					sheet={sheet.data}
					selectedWorkerId={drawerOpen ? selectedWorkerId : undefined}
					onSelectWorker={onSelectWorker}
				/>
			</Card>
		);
	}

	return (
		<div className="timesheet-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Folha de horas</h1>
					<p className="page-subtitle">
						Fechamento mensal de presenças, horas e extras da equipe
						{sheet.data ? ` · ${sheet.data.worksite.name}` : ""}.
					</p>
				</div>
				<div className="page-header__actions">
					<ExportButtons month={month} disabled={!sheet.isSuccess} />
				</div>
			</div>

			<div className="timesheet-page__toolbar">
				<MonthPicker
					value={month}
					max={currentMonth()}
					onChange={onMonthChange}
				/>
			</div>

			<PendingNotice count={pendingDays(rows)} obraKey={obraKey} />

			{body()}

			<WorkerDayDrawer
				open={drawerOpen}
				onOpenChange={setDrawerOpen}
				row={selectedRow}
				month={month}
				holidays={holidays}
				canUpdate={canUpdate}
			/>
		</div>
	);
}
